import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppWindow, Film, Maximize2, Mic, MicOff, Minimize2, PictureInPicture, PictureInPicture2, ScreenShareOff, Type, Users, Volume2, VolumeX, Undo2, Scan, Ratio,
} from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { fmtElapsed, sessionWarning } from '../../lib/share/peer';
import { docPipSupported, videoPipSupported, windowOptions } from '../../lib/share/pip';
import { Avatar } from '../Avatar';
import { noticeText, useShare } from './ShareProvider';

/**
 * EL VISOR "EN GRANDE EN EL MEDIO" (z 450): capa fija bajo `.top-banners` (500) — una alerta de
 * no-show, un SMS escalado o el EOM siguen apareciendo encima del video — y encima del drawer, el
 * tab bar móvil y el sheet del roster. Los controles van ABAJO para que un banner de arriba nunca
 * los tape. Esc MINIMIZA (no sale): la miniatura de 320×180 sigue reproduciendo (y sonando) abajo a
 * la derecha y el que mira sigue trabajando en el claim.
 *
 * Audio (spec §4.10): el <video> arranca con sonido; si el navegador exige gesto (NotAllowedError),
 * el provider lo deja mudo y acá sale el botón "Tap to hear audio" (grande en el teléfono). Arriba,
 * el chip dice "Audio on" o "Muted by Ana" (del `micMuted` que viaja en `share.update`).
 * Ida y vuelta: el segundo chip es MI micrófono ("Your microphone is on — Ana can hear you" / "No
 * microphone") y abajo va el Mic/MicOff propio o, sin mic, "Turn on my microphone" (el clic es el gesto).
 *
 * Ventanas (spec §4.6): "Open in a window" SIEMPRE y primero en los cinco navegadores; "Pop out"
 * (Document PiP) donde exista; PiP clásico del <video> como tercera opción. El inspector no ve
 * ninguna: su player va de borde a borde con controles de 44 px.
 *
 * El <video> es del ShareProvider (no de React): acá solo se monta en el contenedor y se devuelve
 * cuando vuelve del Document PiP.
 */
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function lastInputAt(): number {
  try {
    const v = Number(localStorage.getItem('ppag:lastInput'));
    if (Number.isFinite(v) && v > 0) return v;
  } catch { /* */ }
  return Date.now();
}

export function ShareStage() {
  const { t } = useI18n();
  const s = useShare();
  const a = s.active!;
  const name = a.sharer.name;
  // El minimizado vive en el provider: el botón de la barra ("Viewing {name}") también lo restaura.
  const min = s.minimized;
  const setMin = s.setMinimized;
  const [fit, setFit] = useState<'fit' | 'actual'>('fit');
  const [fs, setFs] = useState(false);
  const [, tick] = useState(0);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [warn, setWarn] = useState<ReturnType<typeof sessionWarning>>(null);
  const holder = useRef<HTMLDivElement>(null);
  const miniHolder = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number; w: number; h: number } | null>(null);
  const quiet = reduceMotion();

  // Montar el <video> del provider en el contenedor que toque (grande o miniatura), y volver a
  // montarlo cuando regresa del PiP (poppedOut → null) o de la ventana propia ("Bring back").
  // SIN lista de dependencias a propósito: el componente sigue montado mientras muestra la píldora
  // "Viewing in a separate window" y al volver al visor ninguna dependencia cambiaba, así que el
  // efecto no corría y el video quedaba suelto (medido en el e2e). La comprobación es barata e
  // idempotente, así que correr tras cada render no cuesta nada.
  useEffect(() => {
    const v = s.videoEl;
    if (!v || s.poppedOut === 'doc_pip') return;
    const target = min ? miniHolder.current : holder.current;
    if (target && v.parentElement !== target) target.appendChild(v);
    v.classList.toggle('is-actual', fit === 'actual' && !min);
  });

  useEffect(() => {
    const id = window.setInterval(() => {
      tick((n) => n + 1);
      setWarn(sessionWarning({ sessionCutAt: s.sessionCutAt, idleLimitMs: s.idleLimitMs, lastInputAt: lastInputAt(), now: Date.now() }));
    }, 1000);
    return () => clearInterval(id);
  }, [s.sessionCutAt, s.idleLimitMs]);

  // Pantalla completa: se sigue el estado real del documento (el usuario puede salir con Esc nativo).
  useEffect(() => {
    const on = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);

  const toggleFs = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void el.requestFullscreen?.().catch(() => {});
  }, []);

  // Atajos: Esc minimiza, F pantalla completa, M minimizar/restaurar, 0 ajustar, 1 tamaño real.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') { if (!min && !document.fullscreenElement && !s.windowMode) { e.preventDefault(); setMin(true); } }
      else if (e.key === 'f' || e.key === 'F') { if (!min) { e.preventDefault(); toggleFs(); } }
      else if (e.key === 'm' || e.key === 'M') { e.preventDefault(); setMin(!min); }
      else if (e.key === '0') { if (!min) setFit('fit'); }
      else if (e.key === '1') { if (!min) setFit('actual'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [min, setMin, toggleFs, s.windowMode]);

  // Miniatura arrastrable (pointer events; funciona con mouse y dedo).
  const onDragStart = (e: React.PointerEvent) => {
    const el = e.currentTarget as HTMLElement;
    const r = el.getBoundingClientRect();
    // Tamaño REAL de la miniatura (320×180 en escritorio, 220×124 en ≤900 px por CSS): con la
    // constante de escritorio el clamp no dejaba llevarla al borde derecho en un teléfono.
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width, h: r.height };
    el.setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const { w, h } = drag.current;
    const x = Math.min(window.innerWidth - w - 8, Math.max(8, e.clientX - drag.current.dx));
    const y = Math.min(window.innerHeight - h - 8, Math.max(8, e.clientY - drag.current.dy));
    setPos({ x, y });
  };
  const onDragEnd = () => { drag.current = null; };

  const since = s.connectedAt ?? (a.acceptedAt ? Date.parse(a.acceptedAt) : Date.now());
  const live = s.peerPhase === 'live';
  const q = s.stats?.quality ?? 'good';
  const qualityText = t(`share.quality.${q}`);
  const connecting = !live && (s.peerPhase === 'new' || s.peerPhase === 'connecting');
  const reconnecting = s.peerPhase === 'disconnected' || s.peerPhase === 'restarting';
  const failed = s.peerPhase === 'failed';
  // ¿El sharer manda algún audio? (voz y/o sistema; el `withAudio` viejo ya no viaja)
  const hasAudio = a.audioMic || a.audioSystem;
  const buttons = windowOptions({ docPip: docPipSupported(), videoPip: videoPipSupported(), field: s.fieldMode, windowMode: s.windowMode });

  // ── Viewer cuyo video se fue a otra pestaña/ventana ──
  if (s.elsewhere && !s.mine) {
    return (
      <div className="ss-pill viewer-else" role="status">
        <span>{s.everMine ? t('share.inWindow') : t('share.answeredElsewhere')}</span>
        <button type="button" className="btn ghost sm" onClick={s.bringBack}><Undo2 size={14} strokeWidth={2} /> {t('share.bringBack')}</button>
        <button type="button" className="btn ghost sm" onClick={s.leave}><ScreenShareOff size={14} strokeWidth={2} /> {t('share.leave')}</button>
      </div>
    );
  }

  // ── Minimizado: miniatura 320×180 abajo a la derecha, sigue reproduciendo Y sonando ──
  if (min && !s.windowMode) {
    const style = pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined;
    return (
      <div className="ss-mini" style={style} onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd} role="region" aria-label={t('share.screenOf', { name })}>
        <div className="ss-mini-video" ref={miniHolder} />
        <div className="ss-mini-bar">
          <span className={`ss-qdot ${q}`} role="img" title={qualityText} aria-label={qualityText} />
          <span className="ss-mini-name">{name}</span>
          {hasAudio && (
            <button type="button" className="btn ghost sm" onPointerDown={(e) => e.stopPropagation()}
              onClick={() => (s.audioBlocked ? s.unmute() : s.setViewerMuted(!s.viewerMuted))}
              title={s.audioBlocked ? t('share.unmute') : s.viewerMuted ? t('share.soundOn') : t('share.soundOff')}
              aria-label={s.audioBlocked ? t('share.unmute') : s.viewerMuted ? t('share.soundOn') : t('share.soundOff')}>
              {s.viewerMuted || s.audioBlocked ? <VolumeX size={13} strokeWidth={2} /> : <Volume2 size={13} strokeWidth={2} />}
            </button>
          )}
          {/* MI micrófono también minimizado: el track sigue viajando al sharer con el visor abajo a la
              derecha (Esc), así que el mute tiene que estar a mano sin restaurar. Rojo = silenciado. */}
          {s.viewerMic === 'on' && (
            <button type="button" className={`btn ghost sm ss-mymic${s.viewerMicMuted ? ' is-muted' : ''}`} onPointerDown={(e) => e.stopPropagation()} onClick={s.toggleViewerMic}
              title={s.viewerMicMuted ? t('share.unmuteMe') : t('share.muteMe')} aria-label={s.viewerMicMuted ? t('share.unmuteMe') : t('share.muteMe')} aria-pressed={s.viewerMicMuted}>
              {s.viewerMicMuted ? <MicOff size={13} strokeWidth={2} /> : <Mic size={13} strokeWidth={2} />}
            </button>
          )}
          <button type="button" className="btn ghost sm" onPointerDown={(e) => e.stopPropagation()} onClick={() => setMin(false)} title={t('share.restore')} aria-label={t('share.restore')}><Maximize2 size={13} strokeWidth={2} /></button>
          <button type="button" className="btn ghost sm" onPointerDown={(e) => e.stopPropagation()} onClick={s.leave} title={t('share.leave')} aria-label={t('share.leave')}><ScreenShareOff size={13} strokeWidth={2} /></button>
        </div>
      </div>
    );
  }

  const popped = s.poppedOut === 'doc_pip';

  return (
    <div className={`ss-stage${s.windowMode ? ' is-window' : ''}${s.fieldMode ? ' is-field' : ''}`} ref={stageRef} role="region" aria-label={t('share.screenOf', { name })}>
      {/* Bandas de estado arriba del todo (bajo cualquier banner de la app). */}
      {s.socketReconnecting && <div className="ss-band gray">{t('share.serverRestart')}</div>}
      {warn && (
        <button type="button" className="ss-band warn" onClick={s.refreshState}>
          {warn.kind === 'idle' ? t('share.sessionSoon') : t('share.sessionCut', { time: new Date(warn.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) })}
        </button>
      )}
      {/* Autoplay bloqueó el sonido: banda ancha y clara sobre el video (además del chip), porque el
          reporte del dueño fue "no se escucha" sin que nadie tocara el botón chico. Solo en escritorio:
          en el teléfono (field mode) manda el botón grande del centro (`.ss-unmute.big`, único allí) y
          esta banda además con verbo "Click" chocaría con su "Tap" en 375 px. */}
      {hasAudio && live && s.audioBlocked && !s.fieldMode && (
        <button type="button" className="ss-band audio-blocked" onClick={s.unmute}>
          <Volume2 size={14} strokeWidth={2} aria-hidden /> {t('share.clickToHear', { name })}
        </button>
      )}

      <div className="ss-head">
        <Avatar userId={a.sharer.id} name={name} size={22} noCall />
        <b className="ss-title">{t('share.screenOf', { name })}</b>
        {live && <span className={`ss-chip live${quiet ? '' : ' gap-pulse'}`}>{t('share.live')}</span>}
        {live && <span className="ss-mono">{fmtElapsed(Date.now() - since)}</span>}
        {/* Malla: cuántos MÁS están mirando el mismo broadcast (spec §14). */}
        {live && (a.viewerCount ?? 0) > 1 && <span className="ss-chip"><Users size={11} strokeWidth={2} aria-hidden /> {t('share.othersWatching', { n: (a.viewerCount ?? 1) - 1 })}</span>}
        {/* `role="img"`: un <span> genérico con aria-label no se lee; así el lector anuncia la calidad. */}
        {live && <span className={`ss-qdot ${q}`} role="img" title={qualityText} aria-label={qualityText} />}
        {s.stats?.relayUsed && <span className="ss-chip relay">{t('share.relayed')}</span>}
        {q === 'poor' && live && <span className="ss-chip slow">{t('share.slow')}</span>}
        {/* Chip de audio: bloqueado por el autoplay → botón; el sharer se muteó → "Muted by"; si no, "Audio on".
            En el teléfono el botón grande del centro (`.ss-unmute.big`) es el único: dos "Tap to hear audio"
            se peleaban los 375 px de la cabecera. */}
        {hasAudio && live && s.audioBlocked && !s.fieldMode && (
          <button type="button" className="ss-chip audio" onClick={s.unmute} style={{ cursor: 'pointer', border: 0, font: 'inherit' }}>
            <Volume2 size={11} strokeWidth={2} aria-hidden /> {t('share.unmute')}
          </button>
        )}
        {hasAudio && live && !s.audioBlocked && a.micMuted && (
          <span className="ss-chip muted" title={t('share.mutedBy', { name })}><VolumeX size={11} strokeWidth={2} aria-hidden /> {t('share.mutedBy', { name })}</span>
        )}
        {hasAudio && live && !s.audioBlocked && !a.micMuted && (
          <span className="ss-chip audio"><Volume2 size={11} strokeWidth={2} aria-hidden /> {t('share.audioOn')}</span>
        )}
        {/* MI micrófono (ida y vuelta): verde cuando viaja; gris cuando no hay (negado / sin dispositivo /
            sin gesto). Muteado por mí no lleva chip: lo dice el botón de abajo en rojo. En ≤900 px queda
            solo el icono (CSS). */}
        {live && s.viewerMic === 'on' && !s.viewerMicMuted && (
          <span className="ss-chip mic" title={t('share.viewerMicOn', { name })}><Mic size={11} strokeWidth={2} aria-hidden /> <span>{t('share.viewerMicOn', { name })}</span></span>
        )}
        {live && s.viewerMic !== 'on' && (
          <span className="ss-chip muted mic-off" title={s.viewerMic === 'denied' ? t('share.micDenied') : t('share.viewerNoMic', { name })}><MicOff size={11} strokeWidth={2} aria-hidden /> <span>{t('share.viewerNoMic', { name })}</span></span>
        )}
      </div>

      <div className={`ss-body${fit === 'actual' ? ' is-actual' : ''}`}>
        <div className="ss-video-holder" ref={holder} />
        {popped && <div className="ss-overlay"><span>{t('share.poppedOut')}</span></div>}
        {connecting && <div className="ss-overlay"><span>{t('share.connecting', { name })}</span></div>}
        {reconnecting && <div className="ss-overlay"><span>{t('share.reconnecting')}</span></div>}
        {failed && <div className="ss-overlay"><span>{t('share.lost', { name })}</span></div>}
        {s.audioBlocked && live && (
          <button type="button" className={`btn ss-unmute${s.fieldMode ? ' big' : ''}`} onClick={s.unmute}><Volume2 size={s.fieldMode ? 20 : 15} strokeWidth={2} /> {t('share.unmute')}</button>
        )}
        {s.notice && (
          <div className="ss-inline-notice on-stage" onClick={s.dismissNotice}>{noticeText(s.notice, t)}</div>
        )}
      </div>

      <div className="ss-controls">
        {/* Ventanas, en el orden que decide `windowOptions` (Open in a window siempre primero). */}
        {buttons.map((b) => b === 'openWindow' ? (
          <button key={b} type="button" className="btn ghost sm ss-desktop-only" onClick={s.openInWindow} title={t('share.openWindow')} aria-label={t('share.openWindow')}>
            <AppWindow size={15} strokeWidth={1.9} /><span>{t('share.openWindow')}</span>
          </button>
        ) : b === 'popOut' ? (
          <button key={b} type="button" className="btn ghost sm ss-desktop-only" onClick={() => { void s.popOut(); }} title={t('share.popOut')} aria-label={t('share.popOut')}>
            <PictureInPicture2 size={15} strokeWidth={1.9} /><span>{t('share.popOut')}</span>
          </button>
        ) : (
          <button key={b} type="button" className="btn ghost sm ss-desktop-only" onClick={() => { void s.popOutVideo(); }} title={t('share.videoPip')} aria-label={t('share.videoPip')}>
            <PictureInPicture size={15} strokeWidth={1.9} /><span>{t('share.videoPip')}</span>
          </button>
        ))}
        {/* Controles: `aria-label` en todos porque en el teléfono / ≤900 px el <span> del texto va con
            display:none y `title` era el único nombre (en táctil nunca se muestra). */}
        <button type="button" className="btn ghost sm" onClick={() => setFit(fit === 'fit' ? 'actual' : 'fit')} title={fit === 'fit' ? t('share.actual') : t('share.fit')} aria-label={fit === 'fit' ? t('share.actual') : t('share.fit')}>
          {fit === 'fit' ? <Ratio size={15} strokeWidth={1.9} /> : <Scan size={15} strokeWidth={1.9} />}<span>{fit === 'fit' ? t('share.actual') : t('share.fit')}</span>
        </button>
        <button type="button" className="btn ghost sm" onClick={toggleFs} title={fs ? t('share.exitFullscreen') : t('share.fullscreen')} aria-label={fs ? t('share.exitFullscreen') : t('share.fullscreen')}>
          <Maximize2 size={15} strokeWidth={1.9} /><span>{fs ? t('share.exitFullscreen') : t('share.fullscreen')}</span>
        </button>
        <button type="button" className="btn ghost sm" onClick={() => s.setHint(s.hint === 'detail' ? 'motion' : 'detail')} title={s.hint === 'detail' ? t('share.smoother') : t('share.sharper')} aria-label={s.hint === 'detail' ? t('share.smoother') : t('share.sharper')}>
          {s.hint === 'detail' ? <Film size={15} strokeWidth={1.9} /> : <Type size={15} strokeWidth={1.9} />}<span>{s.hint === 'detail' ? t('share.smoother') : t('share.sharper')}</span>
        </button>
        {hasAudio && (
          <span className="ss-vol">
            <button type="button" className="btn ghost sm" onClick={() => (s.audioBlocked ? s.unmute() : s.setViewerMuted(!s.viewerMuted))}
              title={s.audioBlocked ? t('share.unmute') : s.viewerMuted ? t('share.soundOn') : t('share.soundOff')}
              aria-label={s.audioBlocked ? t('share.unmute') : s.viewerMuted ? t('share.soundOn') : t('share.soundOff')} aria-pressed={s.viewerMuted}>
              {s.viewerMuted || s.audioBlocked ? <VolumeX size={15} strokeWidth={1.9} /> : <Volume2 size={15} strokeWidth={1.9} />}
              <span>{t('share.volume')}</span>
            </button>
            {!s.fieldMode && (
              <input type="range" min={0} max={1} step={0.05} value={s.viewerMuted ? 0 : s.volume} aria-label={t('share.volume')}
                onChange={(e) => s.setVolume(Number(e.target.value))} />
            )}
          </span>
        )}
        {/* MI micrófono (ida y vuelta): Mute/Unmute propio cuando viaja; "Turn on my microphone" cuando
            no se pudo pedir (sin gesto, negado); nada si no hay dispositivo. */}
        {s.viewerMic === 'on' ? (
          <button type="button" className={`btn ghost sm ss-mymic${s.viewerMicMuted ? ' is-muted' : ''}`} onClick={s.toggleViewerMic}
            title={s.viewerMicMuted ? t('share.unmuteMe') : t('share.muteMe')} aria-label={s.viewerMicMuted ? t('share.unmuteMe') : t('share.muteMe')} aria-pressed={s.viewerMicMuted}>
            {s.viewerMicMuted ? <MicOff size={15} strokeWidth={1.9} /> : <Mic size={15} strokeWidth={1.9} />}<span>{s.viewerMicMuted ? t('share.unmuteMe') : t('share.muteMe')}</span>
          </button>
        ) : s.viewerMic !== 'none' ? (
          <>
            {/* 'denied' ≠ 'off': tras negar el permiso, Chromium rechaza `getUserMedia` al instante sin
                volver a preguntar y el botón parecía no hacer nada. Se deja (Safari/Firefox sí vuelven a
                preguntar) pero con la nota de que el bloqueo está en el candado del navegador. */}
            <button type="button" className={`btn ghost sm ss-mymic${s.viewerMic === 'denied' ? ' is-denied' : ''}`} onClick={s.turnOnMic}
              title={s.viewerMic === 'denied' ? `${t('share.turnOnMic')} — ${t('share.micDenied')}` : t('share.turnOnMic')}
              aria-label={s.viewerMic === 'denied' ? `${t('share.turnOnMic')} — ${t('share.micDenied')}` : t('share.turnOnMic')}>
              {s.viewerMic === 'denied' ? <MicOff size={15} strokeWidth={1.9} /> : <Mic size={15} strokeWidth={1.9} />}<span>{t('share.turnOnMic')}</span>
            </button>
            {s.viewerMic === 'denied' && <span className="ss-pill-note warn ss-mic-note ss-desktop-only" role="status">{t('share.micDenied')}</span>}
          </>
        ) : null}
        {!s.windowMode && (
          <button type="button" className="btn ghost sm" onClick={() => setMin(true)} title={t('share.minimize')} aria-label={t('share.minimize')}>
            <Minimize2 size={15} strokeWidth={1.9} /><span>{t('share.minimize')}</span>
          </button>
        )}
        <button type="button" className="btn ghost sm ss-leave" onClick={s.leave} title={t('share.leave')} aria-label={t('share.leave')}>
          <ScreenShareOff size={15} strokeWidth={1.9} /><span>{t('share.leave')}</span>
        </button>
      </div>
    </div>
  );
}
